import { Scene, AbstractMesh, VideoTexture, Vector4, TransformNode, Material, StandardMaterial, ShaderMaterial, Vector3, PlaneBuilder, Quaternion, MeshBuilder, Color3, Engine } from "babylonjs";
import { Target8w } from "@webcore/core/targets/target.manager.8w";
import { initAlphaVideoShaderMaterial } from "@webcore/content/core/content.common";
import { IAssetBase, IAssetVideo, IAssetModel, IAssetAudio, IAssetTexture } from "@webcore/core/loader/loader";
import emitter from "@webcore/core/events/eventemitter";

import { ContentItem } from "@core/content/types";
import { AppMode } from "@core/context/types";
import AppEvents from "@core/events/events";
import { ContentInstance } from "@core/content/types";
import { ContentPlaybackController } from "@core/content/ContentPlaybackController";
import { Data } from "@core/content/types";
import { getCurrentMode, getCurrentState, getCurrentRecordingState } from "@core/context/AppContextStore";
import { AppState, RecordingState } from "@core/context/types";
import { loadSrtFile } from "@core/utils/subtitles/parseSrt";

export interface ContentInstanceManagerOptions {
  initialContentId?: string;
  /** Whether content should auto-play when loaded/anchored (default: true) */
  autoPlay?: boolean;
  resetAnimationsOnLoad?: boolean;
}
/**
 * Manages content instances, video resources, and scene graph hierarchy
 */
export class ContentInstanceManager {
  private scene: Scene;
  private loadContents: (assets: IAssetBase[]) => Promise<void>;
  private unloadContents: (assets: IAssetBase[]) => void;
  private getAssetByIdOrPath: (idOrPath: string) => IAssetBase | undefined;
  
  public currentInstance?: ContentInstance;
  private isCreatingInstance: boolean = false;
  private currentCreateOperation?: Promise<void>;
  private pendingSwitchContentId?: string;
  private switchContentOperation?: Promise<ContentInstance | undefined>;
  private playbackController?: ContentPlaybackController; // Will be set via initialize
  
  // Multi-instance support: track all loaded instances by content ID
  private instances: Map<string, ContentInstance> = new Map();

  // Track pending disposals to prevent race conditions when re-creating instances
  private pendingDisposals: Map<string, Promise<void>> = new Map();

  // Map to preserve ContentItem data through asset loading
  private assetContentItemMap: Map<string, ContentItem> = new Map();

  // Shared media resources
  private sharedVideoElement: HTMLVideoElement;
  private sharedAudioElement: HTMLAudioElement;
  private sharedVideoPlane?: AbstractMesh;
  private sharedVideoMaterial?: Material;

  private contentData: Data[];

 
  private autoPlay: boolean = true;
  private resetAnimationsOnLoad: boolean = false;
  private anchorActive: boolean = false;
  private hasBeenPlaced: boolean = false; // Tracks if content has been placed at least once

  private canStartPlayback(): boolean {
    const state = getCurrentState();
    const recordingState = getCurrentRecordingState();
    return state === AppState.EXPERIENCE && recordingState !== RecordingState.PREVIEW;
  }

  /** Convert config vectors (plain object or Vector3) to Babylon Vector3. */
  private toVector3(value: ContentItem["position"] | undefined, fallback: Vector3): Vector3 {
    if (!value) return fallback.clone();
    if (value instanceof Vector3) return value.clone();
    return new Vector3(value.x, value.y, value.z);
  }

  constructor(
    contentData: Data[],
    scene: Scene,
    loadContents: (assets: IAssetBase[]) => Promise<void>,
    unloadContents: (assets: IAssetBase[]) => void,
    getAssetByIdOrPath: (idOrPath: string) => IAssetBase | undefined
  ) {
    this.contentData = contentData;
    this.scene = scene;
    this.loadContents = loadContents;
    this.unloadContents = unloadContents;
    this.getAssetByIdOrPath = getAssetByIdOrPath;
    this.sharedVideoElement = this.initSharedVideoElement();
    this.sharedAudioElement = this.initSharedAudioElement();
    this.initSharedVideoPlane();
    this.initAnchorEventListeners();
    this.initVisibilityListeners();
  }

  /**
   * Initialize content system with playback controller and optional initial content
   */
  public async initialize(playbackController: ContentPlaybackController, options?: ContentInstanceManagerOptions): Promise<void> {
    this.playbackController = playbackController;
    this.autoPlay = options?.autoPlay ?? true;
    this.resetAnimationsOnLoad = options?.resetAnimationsOnLoad ?? false;

     if (options?.initialContentId) {
      await this.initGlobalContent(options.initialContentId);
    }
  }

  /**
   * Initialize global content to be used across all modes
   */
  private async initGlobalContent(id: string): Promise<void> {
    try {
      const instance = await this.createInstance(id, undefined);

      if (instance.isLoaded && this.playbackController) {
         if (this.autoPlay) {
          await this.playContent(instance);
        }
        else{
          this.playbackController?.hide(instance);
        }
      }
    } catch (error) {
      console.error(`Failed to create content for ${id}:`, error);
    }
  }

  /**
   * Automatically pause/resume the current instance when page visibility changes.
   * This keeps audio/video/model playback in sync with tab visibility at the core level.
   */
  private initVisibilityListeners(): void {
    if (typeof document === "undefined") return;

    const handleVisibilityChange = () => {
      if (!this.playbackController) return;

      if (document.visibilityState === "hidden") {
        this.pauseCurrent();
      } else if (document.visibilityState === "visible") {
        const state = getCurrentState();
        const recordingState = getCurrentRecordingState();
        // Don't resume if we're in recording preview mode
        if (state === AppState.EXPERIENCE && recordingState !== RecordingState.PREVIEW) {
          this.resumeCurrent().catch(err => {
            console.error("Failed to resume content after visibility change:", err);
          });
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
  }

  private async playContent(instance: ContentInstance, emitPlayEvent: boolean = true): Promise<void> {
    if (!this.canStartPlayback()) {
      return;
    }

    // console.log("ContentInstanceManager: Playing content for:", instance.id);
    // await this.playbackController?.play(instance); // TODO check this
    await this.playbackController?.restart(instance);
    
    // Re-setup loop detection after restart (restart cleans it up)
    this.playbackController?.setupContentLoopDetection(instance);

    if (emitPlayEvent) {
      emitter.emit("content-play", instance.target.name);
      this.playbackController?.markPlayEmitted(instance.id);
    }
  }

  /**
   * Stop playback completely for the current content instance (if any)
   */
  public stopCurrent(): void {
    if (this.currentInstance && this.playbackController) {
      // this.playbackController.stop(this.currentInstance);
      this.playbackController.stop(this.currentInstance, true);

    }
  }

  /**
   * Pause playback for the current content instance (if any)
   */
  public pauseCurrent(): void {
    if (this.currentInstance && this.playbackController) {
      this.playbackController.pause(this.currentInstance);
    }
  }

  /**
   * Resume playback for the current content instance (if any)
   * Also ensures content is visible (in case it was hidden while paused)
   */
  public async resumeCurrent(): Promise<void> {
    // const currentState = getCurrentState();
    // if (currentState !== AppState.EXPERIENCE) {
    //   return;
    // } TODO check this
    
    if (!this.canStartPlayback()) {
      return;
    }

    if (this.currentInstance && this.currentInstance.isLoaded && this.playbackController) {
      await this.playbackController.resume(this.currentInstance); 

    }
  }

  /**
   * Restart playback from beginning for the current content instance (manual loop)
   */
  public async restartCurrent(): Promise<void> {
    // const currentState = getCurrentState();
    // if (currentState !== AppState.EXPERIENCE) {
    //   return;
    // } TODO check this
    
    if (!this.canStartPlayback()) {
      return;
    }

    if (this.currentInstance && this.currentInstance.isLoaded && this.playbackController) {
      await this.playbackController.restart(this.currentInstance);
      // Re-setup loop detection after restart (restart cleans it up)
      this.playbackController.setupContentLoopDetection(this.currentInstance);
    }
  }

  /**
   * Hide the current content instance (e.g. during video preview)
   */
  public hideCurrent(): void {
    if (this.currentInstance && this.playbackController) {
      this.playbackController.hide(this.currentInstance);
    }
  }

  /**
   * Show the current content instance
   */
  public showCurrent(): void {
    if (this.currentInstance && this.currentInstance.isLoaded && this.playbackController) {
      this.playbackController.show(this.currentInstance);
    }
  }

  /**
   * Listen to anchor events and handle content parenting
   */
  private initAnchorEventListeners(): void {
    AppEvents.on("anchor-created", (anchor) => {
      //console.log("ContentInstanceManager: anchor-created", anchor.name, "mode:", getCurrentMode());
      
      const instance = this.currentInstance;
      if (!instance) {
        //console.log("ContentInstanceManager: No current instance");
        return;
      }
      
      //console.log("ContentInstanceManager: Instance type:", instance.contentItem.type, "id:", instance.contentItem.id);
      
      const currentMode = getCurrentMode();
      
      // Check if this is video content on image tracking mode
      const isVideoOnImageTracking = currentMode === AppMode.IMAGE &&
                                      instance.contentItem.type === "image" && 
                                      instance.contentItem.content?.some((c: any) => c.type === "video");
      
      if (isVideoOnImageTracking) {
        //console.log("ContentInstanceManager: Video on image tracking - not parenting");
        // Set anchor active flag for video on image tracking (needed for playback logic)
        this.anchorActive = true;
        // Mark content as placed when anchor is created (for image tracking, anchor creation = placement)
        this.hasBeenPlaced = true;
        
        // Play content if loaded (anchor created means we're ready to play)
        if (this.autoPlay && instance.isLoaded && this.canStartPlayback()) {
          this.playContent(instance);
        }
      } else {
        //console.log("ContentInstanceManager: Parenting content to anchor");
        
        // Reset transform if previously unparented (e.g., video switching from image to ground)
        if (!instance.rootNode.parent) {
          //console.log("ContentInstanceManager: Resetting rootNode transform before parenting");
          instance.rootNode.position.set(0, 0, 0);
          instance.rootNode.rotation.set(0, 0, 0);
          instance.rootNode.scaling.set(1, 1, 1);
        }
        
        // ALWAYS parent to anchor and set active flag - this must happen regardless of state
        this.setParentAnchor(anchor);
        this.anchorActive = true;
        //console.log("ContentInstanceManager: Parent set to:", instance.rootNode.parent?.name);
        
        // Only play content if in EXPERIENCE state and conditions are met
        if (this.autoPlay && instance.isLoaded && this.canStartPlayback()) {
          // const currentState = getCurrentState();
          // if (currentState === AppState.EXPERIENCE) {
            this.playbackController?.show(instance);
            this.playContent(instance);
          // }
        }
      }
    });

    AppEvents.on("anchor-removed", (anchor) => {
      //console.log("ContentInstanceManager: anchor-removed", anchor.name);
      const instance = this.currentInstance;
      // Image-video content is not parented to imageAnchor, so clear the active-anchor flag explicitly.
      if (anchor.name === "imageAnchor") {
        this.anchorActive = false;
      }
      if (instance) {
        const currentParent = instance.rootNode.parent;
        //console.log("ContentInstanceManager: Current parent:", currentParent?.name, "Removed anchor:", anchor.name);
        
        // Only unparent if the removed anchor is actually the current parent
        if (currentParent === anchor) {
          //console.log("ContentInstanceManager: Unparenting content");
          if (!this.autoPlay) {
            this.playbackController?.hide(instance);
          }
          this.setParentAnchor(undefined);
          this.anchorActive = false;
        } else {
          //console.log("ContentInstanceManager: Not unparenting (anchor is not current parent)");
        }
      }
    });

    AppEvents.on("content-positioned", () => {
      this.hasBeenPlaced = true; // Mark that content has been placed at least once
      const instance = this.currentInstance;
      if (instance && instance.isLoaded && this.canStartPlayback()) {
        // Show content (it may have been hidden) and play it
        this.playbackController?.show(instance);
        this.playContent(instance);
      }
    });
  }

  private initSharedVideoElement(): HTMLVideoElement {
    let videoElement = document.getElementById("ejw-video") as HTMLVideoElement;
    if (!videoElement) {
      videoElement = document.createElement("video");
      videoElement.id = "ejw-video";
      videoElement.style.display = "none";
      videoElement.crossOrigin = "anonymous";
      videoElement.loop = false;
      videoElement.muted = true;
      videoElement.playsInline = true;
      document.body.appendChild(videoElement);
    }
    return videoElement;
  }

  private initSharedAudioElement(): HTMLAudioElement {
    let audioElement = document.getElementById("ejw-audio") as HTMLAudioElement;
    if (!audioElement) {
      audioElement = document.createElement("audio");
      audioElement.id = "ejw-audio";
      audioElement.style.display = "none";
      document.body.appendChild(audioElement);
    }

    audioElement.crossOrigin = "anonymous";
    audioElement.preload = "auto";
    audioElement.loop = false;
    audioElement.muted = true;
    audioElement.autoplay = false;
    audioElement.setAttribute("playsinline", "true");

    return audioElement;
  }

  private initSharedVideoPlane(): void {
    this.sharedVideoPlane = PlaneBuilder.CreatePlane("sharedVideoPlane", { size: 1 }, this.scene);
    this.sharedVideoPlane.setEnabled(false);
    this.sharedVideoPlane.isVisible = false;
  }

  /**
   * Create a new content instance
   * @param id Content ID to load
   * @param parentAnchor Optional parent transform node
   * @param setActive Whether to set as current instance and dispose previous (default: true)
   */
  public async createInstance(id: string, parentAnchor?: TransformNode, setActive: boolean = true): Promise<ContentInstance> {
    // Wait for any pending disposal of this content to complete first
    // This prevents "asset already exists" race conditions when scrolling quickly
    const pendingDisposal = this.pendingDisposals.get(id);
    if (pendingDisposal) {
      await pendingDisposal;
    }

    // Check if instance already exists
    const existing = this.instances.get(id);
    if (existing) {
      if (setActive) {
        this.currentInstance = existing;
      }
      return existing;
    }

    if (this.isCreatingInstance) {
      // Another create is currently running. Wait for it, then reuse if available.
      await this.currentCreateOperation;
      const existingAfterWait = this.instances.get(id);
      if (existingAfterWait) {
        if (setActive) {
          this.currentInstance = existingAfterWait;
        }
        return existingAfterWait;
      }
    }

    this.isCreatingInstance = true;
    let resolveCreateOperation: (() => void) | undefined;
    this.currentCreateOperation = new Promise<void>((resolve) => {
      resolveCreateOperation = resolve;
    });

    try {
      const dataItem = this.contentData.find(item => item.id === id);
      if (!dataItem || !dataItem.content || dataItem.content.length === 0) {
        throw new Error(`No content configured for ${id}`);
      }

      // Clean up old instance if setting as active (single-instance mode behavior)
      if (setActive && this.currentInstance) {
        const oldId = this.currentInstance.contentItem?.id;
        await this.disposeInstance(this.currentInstance);
        if (oldId) {
          this.instances.delete(oldId);
        }
        await new Promise(resolve => setTimeout(resolve, 100)); // Small delay for cleanup
      }

      emitter.emit("content-loading", id);

      const instanceId = `${id}_${Date.now()}`;
      const rootNode = new TransformNode(`content_${instanceId}`, this.scene);
      
      if (parentAnchor) {
        rootNode.parent = parentAnchor;
        // Mark anchor as active when passing an existing anchor
        this.anchorActive = true;
      }

      const slamTarget: Target8w = {
        name: id,
        type: "slam",
        data: {},
      } as any;

      const instance: ContentInstance = {
        id: instanceId,
        contentItem: dataItem,
        target: slamTarget,
        rootNode,
        assets: [],
        isVisible: false,
        isLoaded: false,
        assetIds: [],
      };

      // Load subtitles if configured (non-blocking - load in background)
      if (dataItem.subtitle) {
        loadSrtFile(dataItem.subtitle.path, dataItem.subtitle.offsetMs || 0)
          .then(cues => {
            instance.subtitleCues = cues;
          })
          .catch(error => {
            console.warn(`Failed to load subtitles for ${id}:`, error);
          });
      }

      const assets: IAssetBase[] = [];
      dataItem.content.forEach((c: ContentItem, index: number) => {
        let asset: IAssetBase | undefined;
        // Create unique ID for each asset
        const assetId = `${instanceId}_${c.type}_${index}`;
        
        // Store ContentItem in map (will survive through asset loading)
        this.assetContentItemMap.set(assetId, c);
        
        if (c.type === "video") {
          asset = {
            type: "video",
            path: c.path,
            id: assetId,
            videoElement: this.sharedVideoElement,
          } as IAssetVideo;
        } else if (c.type === "model") {
          asset = {
            type: "model",
            path: c.path,
            id: assetId,
          } as IAssetModel;
        } else if (c.type === "image") {
          asset = {
            type: "texture",
            path: c.path,
            id: assetId,
          } as IAssetTexture;
        } else if (c.type === "audio") {
          asset = {
            type: "audio",
            path: c.path,
            id: assetId,
            audioElement: this.sharedAudioElement,
          } as IAssetAudio;
        }
        
        instance.assetIds.push(assetId);

        if(asset) {
          assets.push(asset);
        } else {
          // Unsupported asset type – remove from tracking
          instance.assetIds.pop();
        }
      });

      // Add to instances map
      this.instances.set(id, instance);
      
      // Set as current if requested
      if (setActive) {
        this.currentInstance = instance;
      }

      // Load asset (don't await - loadAssetComplete will handle it)
      this.loadContents(assets).then(() => emitter.emit("content-loaded", instance.id));

      return instance;
    } finally {
      this.isCreatingInstance = false;
      resolveCreateOperation?.();
      this.currentCreateOperation = undefined;
    }
  }

  /**
   * Switch to content by ID.
   * Skips if already showing the same content.
   * Returns the new instance, or current instance if already showing.
   */
  public async switchContent(contentId: string): Promise<ContentInstance | undefined> {
    this.pendingSwitchContentId = contentId;
    if (this.switchContentOperation) {
      return this.switchContentOperation;
    }

    this.switchContentOperation = (async () => {
      while (this.pendingSwitchContentId) {
        const nextContentId = this.pendingSwitchContentId;
        this.pendingSwitchContentId = undefined;

        // Skip if we're already showing this content
        if (this.currentInstance?.contentItem?.id === nextContentId) {
          continue;
        }

        const currentAnchor = this.currentInstance?.rootNode.parent as TransformNode | undefined;
        await this.createInstance(nextContentId, currentAnchor);
      }

      return this.currentInstance;
    })();

    try {
      return await this.switchContentOperation;
    } finally {
      this.switchContentOperation = undefined;
    }
  }

  /**
   * Called when asset loading completes - handles setup and playback
   */
  public onAssetLoadComplete(asset: IAssetBase): void {
    if (!asset.id) {
      console.error("Asset has no id:", asset);
      return;
    }
    
    // Find the instance that owns this asset (supports multi-instance mode)
    let instance: ContentInstance | undefined;
    for (const inst of this.instances.values()) {
      if (inst.assetIds.includes(asset.id)) {
        instance = inst;
        break;
      }
    }
    
    if (!instance) {
      // Asset was loaded but instance was disposed - dispose orphaned asset
      this.disposeOrphanedAsset(asset);
      return;
    }

    // Add asset to the instance's assets array
    instance.assets.push(asset);

    if (asset.type === "video") {
      this.setupVideoPlane(instance, asset as IAssetVideo);
    } else if (asset.type === "model") {
      this.setupModel(instance, asset as IAssetModel);
    } else if (asset.type === "texture") {
      this.setupImagePlane(instance, asset as IAssetTexture);
    }

    // Check if all assets are loaded
    const allAssetsLoaded = instance.assetIds.length === instance.assets.length;
    if (!allAssetsLoaded) {
      //console.log(`Asset ${asset.id} loaded. Waiting for ${instance.assetIds.length - instance.assets.length} more asset(s)...`);
      return;
    }

    instance.isLoaded = true;
    //console.log(`All assets loaded for instance ${instance.id}`);

    if (!this.playbackController) return;

    this.playbackController.setupContentLoopDetection(instance);

    // If autoplay is enabled and content hasn't been placed yet, hide and wait
    if (this.autoPlay && !this.hasBeenPlaced) { // TODO check this
      this.playbackController?.hide(instance);
      return;
    }
    
    // Content has been placed before (or autoplay is false) - auto-play on subsequent loads
    if (this.anchorActive && this.canStartPlayback()) {
      this.playbackController?.show(instance);
      // this.playContent(instance);
      if (this.autoPlay) {
        this.playContent(instance);
      }
      return;
    }
    
    this.playbackController?.hide(instance);
  }

  private setupVideoPlane(instance: ContentInstance, videoAsset: IAssetVideo): void {
    //console.log("setupVideoPlane called for:", videoAsset.id);
    
    if (!videoAsset.videoTexture) {
      console.error("Video texture not loaded in asset:", videoAsset.id);
      return;
    }

    // Dispose previous material to avoid memory leak when switching content
    if (this.sharedVideoMaterial) {
      this.sharedVideoMaterial.dispose();
      this.sharedVideoMaterial = undefined;
    }

    const videoContent = instance.contentItem.content?.find((c: ContentItem) => c.type === "video");
    const hasAlpha = videoContent?.videoHasAlpha;

    this.sharedVideoMaterial = hasAlpha 
      ? this.createAlphaVideoMaterial(videoAsset.videoTexture)
      : this.createOpaqueVideoMaterial(videoAsset.videoTexture);

    // console.log("Video material created:", {
    //   hasAlpha,
    //   materialType: this.sharedVideoMaterial.getClassName()
    // });

    if (this.sharedVideoPlane) {
      this.sharedVideoPlane.material = this.sharedVideoMaterial;
      this.sharedVideoPlane.parent = instance.rootNode;
      instance.videoPlane = this.sharedVideoPlane;
      
      // console.log("Video plane setup:", {
      //   planeName: this.sharedVideoPlane.name,
      //   parentName: instance.rootNode.name,
      //   hasMaterial: !!this.sharedVideoPlane.material
      // });
      
      // Apply ContentItem transform properties (similar to model setup)
      // Retrieve ContentItem from map using asset ID
      const contentItem = videoAsset.id ? this.assetContentItemMap.get(videoAsset.id) : undefined;
      if (contentItem) {
        this.sharedVideoPlane.position.copyFrom(this.toVector3(contentItem.position, new Vector3(0, 0, 0)));
        this.sharedVideoPlane.rotation.copyFrom(this.toVector3(contentItem.rotation, new Vector3(0, 0, 0)));
        this.sharedVideoPlane.scaling.copyFrom(this.toVector3(contentItem.scale, new Vector3(1, 1, 1)));
      } else {
        // Fallback to defaults if no contentItem found
        this.sharedVideoPlane.position.set(0, 0, 0);
        this.sharedVideoPlane.rotation.set(0, 0, 0);
        this.sharedVideoPlane.scaling.set(1, 1, 1);
        console.warn("No ContentItem found for video asset:", videoAsset.id);
      }
    }
  }

  private setupModel(instance: ContentInstance, modelAsset: IAssetModel): void {
    if (!modelAsset.meshRoot) {
      console.error("Model meshRoot not loaded in asset:", modelAsset.id);
      return;
    }

    const wrapperNode = new TransformNode(`${modelAsset.id || 'model'}_wrapper`, this.scene);
    wrapperNode.parent = instance.rootNode;
    
    modelAsset.meshRoot.parent = wrapperNode;
    
    const contentItem = modelAsset.id ? this.assetContentItemMap.get(modelAsset.id) : undefined;
    if (contentItem) {
      wrapperNode.position.copyFrom(this.toVector3(contentItem.position, new Vector3(0, 0, 0)));
      wrapperNode.rotation.copyFrom(this.toVector3(contentItem.rotation, new Vector3(0, 0, 0)));
      wrapperNode.scaling.copyFrom(this.toVector3(contentItem.scale, new Vector3(1, 1, 1)));
    }
    
    modelAsset.meshRoot.setEnabled(true);

    // Configure occluders for VPS content
    if (instance.contentItem.type === "vps") {
      this.configureOccluders(modelAsset);
    }
  }

  private setupImagePlane(instance: ContentInstance, textureAsset: IAssetTexture): void {
    if (!textureAsset.texture) {
      console.error("Texture not loaded in asset:", textureAsset.id);
      return;
    }

    const tex = textureAsset.texture;
    tex.hasAlpha = true;
    tex.vScale = -1; // Flip V so texture isn't upside-down
    tex.uScale = -1; // Flip U so text reads correctly (not mirrored)

    const contentItem = textureAsset.id ? this.assetContentItemMap.get(textureAsset.id) : undefined;
    const contentItemScale = contentItem?.scale;
    const sy = contentItemScale?.y ?? 1;
    const imgW = contentItem?.width ?? 1;
    const imgH = contentItem?.height ?? 1;
    // Use image aspect ratio to avoid stretching; sy controls height, width derived from aspect
    const aspect = imgW / imgH;
    const planeHeight = sy;
    const planeWidth = aspect * sy;

    const plane = MeshBuilder.CreatePlane(
      `${textureAsset.id || 'image'}_plane`,
      { width: planeWidth, height: planeHeight },
      this.scene
    );
    plane.parent = instance.rootNode;

    const material = new StandardMaterial(`${textureAsset.id}_mat`, this.scene);
    material.diffuseTexture = tex;
    material.emissiveTexture = tex;
    material.emissiveColor = new Color3(1, 1, 1);
    material.disableLighting = true;
    material.backFaceCulling = false;
    material.useAlphaFromDiffuseTexture = true;
    material.alphaMode = Engine.ALPHA_COMBINE;
    plane.material = material;

    if (contentItem) {
      plane.position.copyFrom(contentItem.position || new Vector3(0, 0, 0));
      plane.rotation.copyFrom(contentItem.rotation || new Vector3(0, 0, 0));
      plane.scaling.set(1, 1, 1);
    }

    plane.setEnabled(true);

    (textureAsset as any).plane = plane;
    (textureAsset as any).planeMaterial = material;
  }

  private configureOccluders(modelAsset: IAssetModel): void {
    if (!modelAsset.meshRoot) return;

    const traverse = (node: any): void => {
      if (node.name?.toLowerCase().includes("occluder") && node.material) {
        node.material.disableColorWrite = true;
      }
      const children = node.getChildren?.() || [];
      children.forEach(traverse);
    };

    traverse(modelAsset.meshRoot);
  }

  /**
   * Dispose an asset that was loaded but is no longer needed (e.g., instance was switched during load)
   */
  private disposeOrphanedAsset(asset: IAssetBase): void {
    if (asset.type === "model") {
      const modelAsset = asset as IAssetModel;
      if (modelAsset.meshRoot) {
        modelAsset.meshRoot.dispose();
      }
      if (modelAsset.meshes) {
        modelAsset.meshes.forEach(mesh => mesh.dispose());
      }
    }
    if (asset.type === "texture") {
      const textureAsset = asset as IAssetTexture & { plane?: AbstractMesh; planeMaterial?: Material };
      if (textureAsset.plane) {
        textureAsset.plane.dispose();
      }
      if (textureAsset.planeMaterial) {
        textureAsset.planeMaterial.dispose();
      }
      if (textureAsset.texture) {
        textureAsset.texture.dispose();
      }
    }
    if (asset.id) {
      this.assetContentItemMap.delete(asset.id);
    }
  }

  /**
   * Update parent anchor for current instance
   */
  public setParentAnchor(anchor?: TransformNode): void {
    if (this.currentInstance) {
      this.currentInstance.rootNode.parent = anchor || null;
    }
  }

  /**
   * Get the shared video element
   */
  public getSharedVideoElement(): HTMLVideoElement {
    return this.sharedVideoElement;
  }

  /**
   * Get the shared video plane
   */
  public getSharedVideoPlane(): AbstractMesh | undefined {
    return this.sharedVideoPlane;
  }

  // ============================================================================
  // Multi-instance support
  // ============================================================================

  /**
   * Get an instance by content ID
   */
  public getInstance(contentId: string): ContentInstance | undefined {
    return this.instances.get(contentId);
  }

  /**
   * Get all loaded instances
   */
  public getAllInstances(): ContentInstance[] {
    return Array.from(this.instances.values());
  }

  /**
   * Set the active instance (when already loaded)
   */
  public setActiveInstance(contentId: string): void {
    const instance = this.instances.get(contentId);
    if (instance) {
      this.currentInstance = instance;
    }
  }

  /**
   * Dispose an instance by content ID
   */
  public disposeInstanceById(contentId: string): void {
    const instance = this.instances.get(contentId);
    if (instance) {
      // Track the disposal as a promise so createInstance can wait for it
      const disposalPromise = this.disposeInstance(instance).then(() => {
        this.pendingDisposals.delete(contentId);
      });
      this.pendingDisposals.set(contentId, disposalPromise);
      this.instances.delete(contentId);
    }
  }

  /**
   * Dispose of an instance
   */
  public async disposeInstance(instance: ContentInstance): Promise<void> {
    // Stop playback first for clean teardown (stops video, audio, animations)
    if (this.playbackController) {
      this.playbackController.stop(instance, false);
    }

    const assetsToUnload: IAssetBase[] = [];
    
    // Collect all assets to unload and clean up map entries
    instance.assets.forEach(asset => {
      assetsToUnload.push(asset);
      
      // Clean up ContentItem map entry
      if (asset.id) {
        this.assetContentItemMap.delete(asset.id);
      }
      
      // Cancel in-flight loading
      if (asset?.loading && asset.abortCtrl) {
        try {
          asset.abortCtrl.abort();
        } catch (e) {
          console.warn('[ContentInstanceManager] Error aborting asset load:', e);
        }
      }
    });
    
    // Also check for assets that haven't loaded yet
    instance.assetIds.forEach(assetId => {
      // Clean up map entry
      this.assetContentItemMap.delete(assetId);
      
      if (!instance.assets.find(a => a.id === assetId)) {
        const asset = this.getAssetByIdOrPath(assetId);
        if (asset) {
          assetsToUnload.push(asset);
          if (asset.loading && asset.abortCtrl) {
            try {
              asset.abortCtrl.abort();
            } catch (e) {
              console.warn('[ContentInstanceManager] Error aborting asset load:', e);
            }
          }
        }
      }
    });

    // Detach video plane (don't dispose shared resources)
    const hasVideo = instance.assets.some(a => a.type === "video");
    if (hasVideo && this.sharedVideoPlane) {
      this.sharedVideoPlane.parent = null;
      this.sharedVideoPlane.setEnabled(false);
      this.sharedVideoPlane.isVisible = false;
      instance.videoPlane = undefined;
    }

    // Dispose image planes and materials (created by setupImagePlane)
    instance.assets.forEach(asset => {
      if (asset.type === "texture") {
        const textureAsset = asset as IAssetTexture & { plane?: AbstractMesh; planeMaterial?: Material };
        if (textureAsset.plane) {
          textureAsset.plane.dispose();
          (textureAsset as any).plane = undefined;
        }
        if (textureAsset.planeMaterial) {
          textureAsset.planeMaterial.dispose();
          (textureAsset as any).planeMaterial = undefined;
        }
      }
    });

    // Unload all assets
    if (assetsToUnload.length > 0) {
      this.unloadContents(assetsToUnload);
    }

    // Dispose root node
    instance.rootNode.dispose();

    // Clean up references
    if (this.currentInstance === instance) {
      this.currentInstance = undefined;
    }
    
    // Remove from instances map
    const contentId = instance.contentItem?.id;
    if (contentId) {
      this.instances.delete(contentId);
    }
  }

  /**
   * Dispose VPS instance specifically
   */
  public disposeVpsInstance(): void {
    if (!this.currentInstance || this.currentInstance.contentItem?.type !== "vps") {
      return;
    }

    const assetsToUnload = this.currentInstance.assets.filter(a => a.type === "model");
    if (assetsToUnload.length > 0) {
      this.unloadContents(assetsToUnload);
    }

    this.currentInstance.rootNode.dispose();
    this.currentInstance = undefined;
  }

  /**
   * Clean up all resources
   */
  public dispose(): void {
    if (this.currentInstance) {
      this.disposeInstance(this.currentInstance);
    }

    if (this.sharedVideoPlane) {
      this.sharedVideoPlane.dispose();
      this.sharedVideoPlane = undefined;
    }

    if (this.sharedVideoMaterial) {
      this.sharedVideoMaterial.dispose();
      this.sharedVideoMaterial = undefined;
    }
  }

  // Material creation methods
  private createAlphaVideoMaterial(videoTexture: VideoTexture): ShaderMaterial {
    const uvBoxRange = { minU: 0.0, maxU: 1.0, minV: 0.0, maxV: 1.0 };
    const uvBoxColor = {
      minU: uvBoxRange.minU * 0.5,
      maxU: uvBoxRange.maxU * 0.5,
      minV: uvBoxRange.minV,
      maxV: uvBoxRange.maxV,
    };
    const uvBoxAlpha = {
      minU: uvBoxColor.minU + 0.5,
      maxU: uvBoxColor.maxU + 0.5,
      minV: uvBoxColor.minV,
      maxV: uvBoxColor.maxV,
    };

    const material = initAlphaVideoShaderMaterial(
      this.scene,
      videoTexture,
      new Vector4(uvBoxRange.minU, uvBoxRange.minV, uvBoxRange.maxU, uvBoxRange.maxV),
      new Vector4(uvBoxColor.minU, uvBoxColor.minV, uvBoxColor.maxU, uvBoxColor.maxV),
      new Vector4(uvBoxAlpha.minU, uvBoxAlpha.minV, uvBoxAlpha.maxU, uvBoxAlpha.maxV)
    );
    material.backFaceCulling = false;
    return material;
  }

  private createOpaqueVideoMaterial(videoTexture: VideoTexture): StandardMaterial {
    const material = new StandardMaterial("videoMaterial", this.scene);
    material.diffuseTexture = videoTexture;
    material.backFaceCulling = false;
    material.emissiveColor.set(1, 1, 1);
    return material;
  }
}

